import logger from "../utils/logger";
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck, Info, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { Button } from './ui/button';

import API_URL from '../utils/api';

const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchNotifications = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
            const newNotifications = data.notifications || [];
            const newUnread = data.unread_count || 0;
            
            // Browser push notification for new items
            if (newUnread > unreadCount && unreadCount > 0 && 'Notification' in window && Notification.permission === 'granted') {
                const latest = newNotifications.find(n => !n.read);
                if (latest) {
                    new Notification('DIGI-LOANS', { body: latest.message, icon: '/favicon.ico' });
                }
            }
            
            setNotifications(newNotifications);
            setUnreadCount(newUnread);
        } catch (error) {
            // Silently fail
        }
    };

    const markAsRead = async (id) => {
        try {
            await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true });
            fetchNotifications();
        } catch (error) {
            logger.error('Error:', error);
        }
    };

    const markAllRead = async () => {
        try {
            await axios.put(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true });
            fetchNotifications();
        } catch (error) {
            logger.error('Error:', error);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />;
            case 'warning': return <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />;
            default: return <Info className="h-4 w-4 text-brand flex-shrink-0" />;
        }
    };

    const timeAgo = (dateStr) => {
        const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="notification-bell"
            >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-slate-200 shadow-lg z-50" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                        <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Notifications</h3>
                        {unreadCount > 0 && (
                            <button onClick={markAllRead} className="text-xs text-brand hover:text-blue-800 flex items-center gap-1" data-testid="mark-all-read-btn">
                                <CheckCheck className="h-3 w-3" /> Mark all read
                            </button>
                        )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="py-8 text-center text-sm text-slate-500">No notifications</div>
                        ) : (
                            notifications.slice(0, 20).map((n) => (
                                <div
                                    key={n._id}
                                    className={`flex items-start gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${!n.read ? 'bg-blue-50/50' : ''}`}
                                    onClick={() => !n.read && markAsRead(n._id)}
                                    data-testid={`notification-item-${n._id}`}
                                >
                                    <div className="pt-0.5">{getIcon(n.type)}</div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm ${!n.read ? 'font-medium text-slate-900' : 'text-slate-700'}`}>{n.title}</p>
                                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                                        <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                                    </div>
                                    {!n.read && <div className="w-2 h-2 rounded-full bg-brand mt-2 flex-shrink-0" />}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
